import React from "react";
import { useNavigate } from "react-router-dom";
import styles from "./SummerSaleBanner.module.scss";

// Decorative art (final designer assets, 1x + @2x retina):
//   - summer-home-sticker-desktop/mobile => the "SUMMER SALE" lolly sticker (left)
//   - summer-pineapple-palm              => the pineapple + palm tree (right)
//   - summer-pattern-left/right          => faint tiki line-drawings at each edge
import stickerDesktop from "images/heros/Summer Sale/summer-home-sticker-desktop.png";
import stickerDesktop2x from "images/heros/Summer Sale/summer-home-sticker-desktop@2x.png";
import stickerMobile from "images/heros/Summer Sale/summer-home-sticker-mobile.png";
import stickerMobile2x from "images/heros/Summer Sale/summer-home-sticker-mobile@2x.png";
import pineapplePalm from "images/heros/Summer Sale/summer-pineapple-palm.png";
import pineapplePalm2x from "images/heros/Summer Sale/summer-pineapple-palm@2x.png";
// Patterns use the @2x art as a single crisp background (downscaled on standard
// displays); inline CSS backgrounds can't cleanly express image-set with fallback.
import patternLeft from "images/heros/Summer Sale/summer-pattern-left@2x.png";
import patternRight from "images/heros/Summer Sale/summer-pattern-right@2x.png";

import { SUMMER_SALE } from "constants/SummerSale";

/**
 * Full-width Summer Sale promo strip (Figma "Full width banner", node 16-3082).
 * A slim, clickable band: gradient + coloured bars, "30% OFF" headline and tagline,
 * with the lolly sticker overhanging the left and the pineapple/palm tree on the right.
 *
 * Clicking sends the shopper to the shop. There's no discount code — sale prices
 * are reduced directly in Stripe, so the shop already shows the cut prices.
 */
const SummerSaleBanner = ({ onClick }) => {
    const navigate = useNavigate();

    const handleClick = () => {
        if (onClick) {
            onClick();
            return;
        }
        navigate("/shop");
    };

    return (
        <div
            className={styles.summerSaleBanner}
            data-summer-sale-banner="true"
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleClick()}
            aria-label={`${SUMMER_SALE.percent}% off summer sale — shop now`}
        >
            <div className={styles.band}>
                <div
                    className={styles.pattern}
                    style={{ backgroundImage: `url(${patternLeft}), url(${patternRight})` }}
                    aria-hidden="true"
                />
                <img
                    src={stickerDesktop}
                    srcSet={`${stickerDesktop} 1x, ${stickerDesktop2x} 2x`}
                    className={`${styles.sticker} ${styles.stickerDesktop}`}
                    alt="Summer Sale"
                    draggable={false}
                />
                <img
                    src={stickerMobile}
                    srcSet={`${stickerMobile} 1x, ${stickerMobile2x} 2x`}
                    className={`${styles.sticker} ${styles.stickerMobile}`}
                    alt="Summer Sale"
                    draggable={false}
                />

                <div className={styles.text}>
                    <span className={styles.headline}>
                        {SUMMER_SALE.percent}% OFF
                    </span>
                    <div className={styles.copy}>
                        <span className={styles.tagline}>
                            Keep cool and carry on with<br />
                            games on sale all summer long!
                        </span>
                    </div>
                </div>

                <img
                    src={pineapplePalm}
                    srcSet={`${pineapplePalm} 1x, ${pineapplePalm2x} 2x`}
                    className={styles.pineapplePalm}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                />
            </div>

            <div className={styles.bars}>
                <span className={styles.barYellow} />
                <span className={styles.barOrange} />
                <span className={styles.barTeal} />
                <span className={styles.barPink} />
            </div>
        </div>
    );
};

export default SummerSaleBanner;
